/**
 * Image preprocessing utility — applique :
 *   1. Orientation EXIF auto (via canvas drawImage natif)
 *   2. Resize au max `maxDim` px sur le côté le plus long
 *   3. Conversion niveaux de gris + boost contraste (configurable)
 *   4. Filtre de netteté (sharpening 3x3) — optionnel
 *   5. Encodage JPEG qualité 0.9
 *
 * Optimisé pour 2 cas :
 *   - OCR étiquette produit (texte court, contraste fort) → MAX=2000, sharpening ON
 *   - OCR Bon de Livraison (texte petit, beaucoup de lignes) → MAX=2400, contraste doux
 *
 * @param {File|Blob} file - fichier source (issu d'un input file ou canvas.toBlob)
 * @param {Object} options
 * @param {number} [options.maxDim=2000] - Dimension max (px) du côté le plus long
 * @param {boolean} [options.grayscale=true] - Convertir en niveaux de gris
 * @param {boolean} [options.boostContrast=true] - Booster le contraste
 * @param {number} [options.contrastStrength=20] - Force du boost (10=doux, 30=fort)
 * @param {boolean} [options.sharpen=false] - Appliquer un filtre de netteté 3x3
 * @param {number} [options.quality=0.9] - Qualité JPEG (0-1)
 * @returns {Promise<{blob: Blob, dataUrl: string, width: number, height: number}>}
 */
export const preprocessImage = (file, options = {}) => {
  const {
    maxDim = 2000,
    grayscale = true,
    boostContrast = true,
    contrastStrength = 20,
    sharpen = false,
    quality = 0.9,
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Format image non supporté (HEIC ?). Réessayez en JPG.'));
      img.onload = () => {
        try {
          const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
          const w = Math.round(img.width * ratio);
          const h = Math.round(img.height * ratio);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, w, h);

          if (grayscale || boostContrast) {
            try {
              const imgData = ctx.getImageData(0, 0, w, h);
              const d = imgData.data;
              const lo = 100;
              const hi = 160;
              for (let i = 0; i < d.length; i += 4) {
                const gray = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
                let v = grayscale ? gray : null;
                if (boostContrast) {
                  const base = grayscale ? gray : (d[i] + d[i + 1] + d[i + 2]) / 3;
                  if (base < lo) v = Math.max(0, (v ?? base) - contrastStrength);
                  else if (base > hi) v = Math.min(255, (v ?? base) + contrastStrength);
                  else v = v ?? base;
                }
                if (grayscale) {
                  d[i] = d[i + 1] = d[i + 2] = v;
                } else if (boostContrast) {
                  const factor = v / ((d[i] + d[i + 1] + d[i + 2]) / 3 || 1);
                  d[i] = Math.min(255, Math.max(0, d[i] * factor));
                  d[i + 1] = Math.min(255, Math.max(0, d[i + 1] * factor));
                  d[i + 2] = Math.min(255, Math.max(0, d[i + 2] * factor));
                }
              }
              ctx.putImageData(imgData, 0, 0);
            } catch (_) {
              /* CORS canvas tainted — keep original */
            }
          }

          // Filtre de netteté 3x3 (unsharp mask) — crucial pour OCR de petits textes
          if (sharpen) {
            try {
              applySharpenFilter(ctx, w, h);
            } catch (_) {
              /* keep original on failure */
            }
          }

          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error('Conversion JPEG échouée'));
              resolve({
                blob,
                dataUrl: canvas.toDataURL('image/jpeg', quality),
                width: w,
                height: h,
              });
            },
            'image/jpeg',
            quality
          );
        } catch (err) {
          reject(err);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Convolution 3x3 — kernel de netteté (unsharp mask) :
 *     0 -1  0
 *    -1  5 -1
 *     0 -1  0
 * Effet : amplifie les transitions de luminosité = chiffres et lettres deviennent
 * plus nets, idéal pour Tesseract.js qui exige des contours bien définis.
 */
const applySharpenFilter = (ctx, w, h) => {
  const src = ctx.getImageData(0, 0, w, h);
  const dst = ctx.createImageData(w, h);
  const sd = src.data;
  const dd = dst.data;
  const k = [0, -1, 0, -1, 5, -1, 0, -1, 0];
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      let r = 0, g = 0, b = 0;
      let ki = 0;
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const off = ((y + ky) * w + (x + kx)) * 4;
          r += sd[off] * k[ki];
          g += sd[off + 1] * k[ki];
          b += sd[off + 2] * k[ki];
          ki++;
        }
      }
      const off = (y * w + x) * 4;
      dd[off] = Math.min(255, Math.max(0, r));
      dd[off + 1] = Math.min(255, Math.max(0, g));
      dd[off + 2] = Math.min(255, Math.max(0, b));
      dd[off + 3] = 255;
    }
  }
  // Recopie les bords (1px) pour éviter une bordure noire
  for (let x = 0; x < w; x++) {
    for (const y of [0, h - 1]) {
      const off = (y * w + x) * 4;
      dd[off] = sd[off]; dd[off + 1] = sd[off + 1]; dd[off + 2] = sd[off + 2]; dd[off + 3] = 255;
    }
  }
  for (let y = 0; y < h; y++) {
    for (const x of [0, w - 1]) {
      const off = (y * w + x) * 4;
      dd[off] = sd[off]; dd[off + 1] = sd[off + 1]; dd[off + 2] = sd[off + 2]; dd[off + 3] = 255;
    }
  }
  ctx.putImageData(dst, 0, 0);
};

/**
 * Preset adapté au scan de Bons de Livraison (BL) :
 * - Résolution 2400px (texte petit lisible par Claude Vision)
 * - Boost contraste doux (15) — préserve les nuances d'encre manuscrite
 * - Pas de sharpening (Claude Vision est tolérant et le sharpening peut créer du bruit sur photos floues)
 * - JPEG qualité 0.92 (~150-300 Ko/page A4)
 */
export const preprocessBL = (file) =>
  preprocessImage(file, {
    maxDim: 2400,
    grayscale: true,
    boostContrast: true,
    contrastStrength: 15,
    sharpen: false,
    quality: 0.92,
  });

/**
 * Preset adapté à une étiquette produit (OCR Tesseract date péremption / EAN) :
 * - Résolution 2000px (gain +25% vs ancien preset 1600px = chiffres lisibles)
 * - Contraste fort (30)
 * - **Sharpening 3x3** activé — capital pour OCR des petites dates type "EXP 06/2030"
 * - Tesseract.js a besoin de contours nets pour reconnaître les chiffres.
 */
export const preprocessLabel = (file) =>
  preprocessImage(file, {
    maxDim: 2000,
    grayscale: true,
    boostContrast: true,
    contrastStrength: 30,
    sharpen: true,
    quality: 0.92,
  });

/**
 * Preset adapté aux photos justificatives d'écart (pas d'OCR) :
 * - Résolution 1600px (lecture humaine suffit)
 * - Pas de niveaux de gris (couleur préservée pour identification visuelle)
 * - Contraste léger — JPEG 0.85 pour économiser la bande passante.
 */
export const preprocessEvidence = (file) =>
  preprocessImage(file, {
    maxDim: 1600,
    grayscale: false,
    boostContrast: false,
    sharpen: false,
    quality: 0.85,
  });

